import { createAction, createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getOptimizationDraft, hasSucceededRun } from './optimizationDraft';
import type { RootState } from '../../app/store';
import { createThread, getThread, performAction, SophiaThread, getProjectsByCollection, deleteThread, CreationMetadata, ProjectType, CatalogMode, getCatalogModeFromThread, getProjectTypeFromThread, ProjectLockedError } from '../../api/sophia';

export interface ProjectItem {
  threadId: string;
  name: string;
  lastEditTime: number;
  projectType?: ProjectType;
  catalogMode?: CatalogMode;
  /** SPJ-1372: stagione numerica (es. "272"), usata dal filtro "Stagione". */
  season?: string;
  canLoad: boolean;
  loadDisabledReason?: string;
  /** Ottimizzazione con una configurazione iniziata e non ancora lanciata (SPJ-1366). */
  hasDraft?: boolean;
  /** Ottimizzazione con almeno una run completata. */
  hasCompletedRun?: boolean;
  /** SPJ-1372: campi forniti dal nuovo endpoint `/projects`. */
  optimizationId?: string;
  consegna?: string;
  macrotema?: string;
  /** La "LINEA" da mostrare (Uomo/Donna). */
  gender?: string;
  creatorName?: string;
  creatorEmail?: string;
  /** SPJ-1372: ultimo utente che ha modificato il progetto (solo ottimizzazioni). */
  lastEditorName?: string;
  lastEditorEmail?: string;
  lastEditorTime?: number;
  /** SPJ-1372: progetto in uso da un altro utente (non apribile finche' non lo chiude). */
  lockedByName?: string;
  incognito?: boolean;
}

/** SPJ-1372: progetto che non si e' potuto aprire (o che si e' perso) perche' in uso da un altro utente. */
export interface LockedProjectInfo {
  threadId: string;
  lockedByName: string | null;
  lockedByEmail: string | null;
}

export const projectLocked = createAction<LockedProjectInfo>('thread/projectLocked');

interface ThreadSliceState {
  threadId: string | null;
  threadData: SophiaThread | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  availableProjects: ProjectItem[];
  projectsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  projectsError: string | null;
  /**
   * SPJ-1372: segnala che l'editor del Merchandising Plan ha modifiche non salvate.
   * Serve esclusivamente alla guardia di navigazione interna (conferma prima di
   * lasciare l'editor). La UI del bottone "Salva modifiche" usa il flag locale di
   * MerchandisingPlanPage, non questo.
   */
  hasUnsavedMpChanges: boolean;
  /** SPJ-1372: valorizzato quando un progetto risulta in uso da un altro utente (HTTP 423). */
  lockedProject: LockedProjectInfo | null;
}

const STORAGE_KEY = 'thread_id';

const initialState: ThreadSliceState = {
  threadId: null,
  threadData: null,
  status: 'idle',
  error: null,
  availableProjects: [],
  projectsStatus: 'idle',
  projectsError: null,
  hasUnsavedMpChanges: false,
  lockedProject: null,
};

function lockedInfo(threadId: string, error: ProjectLockedError): LockedProjectInfo {
  return { threadId, lockedByName: error.lockedByName, lockedByEmail: error.lockedByEmail };
}

function clearStoredThreadId(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (storageError) {
    console.warn('Unable to clear thread id from localStorage', storageError);
  }
}

function readStoredThreadId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to read thread id from localStorage', error);
    return null;
  }
}

function writeStoredThreadId(threadId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, threadId);
  } catch (error) {
    console.warn('Unable to persist thread id to localStorage', error);
  }
}

function isThreadNotFoundError(error: unknown): boolean {
  if (error instanceof Error) {
    // Check if the error message contains "404" status code
    return error.message.includes('404');
  }
  return false;
}

// Returns both threadId and optionally the fetched thread data to avoid double-fetching
async function ensureThreadIdWithData(existingId?: string | null): Promise<{ threadId: string; thread?: SophiaThread }> {
  if (existingId) {
    // Validate that the existing thread ID is still valid
    try {
      const thread = await getThread(existingId);
      return { threadId: existingId, thread };
    } catch (error) {
      // Only create a new thread if this is a 404 (thread not found)
      // For other errors (500, network, auth, etc.), let them bubble up
      if (isThreadNotFoundError(error)) {
        console.warn('Existing thread ID not found (404), creating new thread:', error);
        // Clear the invalid thread ID from storage
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(STORAGE_KEY);
          } catch (storageError) {
            console.warn('Unable to clear invalid thread id from localStorage', storageError);
          }
        }
        // Fall through to create a new thread
      } else {
        // Re-throw non-404 errors (500, network issues, auth problems, etc.)
        throw error;
      }
    }
  }

  const stored = readStoredThreadId();

  if (stored) {
    // Validate that the stored thread ID is still valid
    try {
      const thread = await getThread(stored);
      return { threadId: stored, thread };
    } catch (error) {
      // Only create a new thread if this is a 404 (thread not found)
      // For other errors (500, network, auth, etc.), let them bubble up
      if (isThreadNotFoundError(error)) {
        console.warn('Stored thread ID not found (404), creating new thread:', error);
        // Clear the invalid thread ID from storage
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(STORAGE_KEY);
          } catch (storageError) {
            console.warn('Unable to clear invalid thread id from localStorage', storageError);
          }
        }
        // Fall through to create a new thread
      } else {
        // Re-throw non-404 errors (500, network issues, auth problems, etc.)
        throw error;
      }
    }
  }

  const threadId = await createThread();
  writeStoredThreadId(threadId);
  return { threadId };
}

// Legacy function for backward compatibility - just returns threadId
async function ensureThreadId(existingId?: string | null): Promise<string> {
  const { threadId } = await ensureThreadIdWithData(existingId);
  return threadId;
}

function getOptimizationProjectLoadability(appState: Record<string, unknown> | undefined): Pick<ProjectItem, 'canLoad' | 'loadDisabledReason' | 'hasDraft' | 'hasCompletedRun'> {
  // SPJ-1366: si apre anche un progetto senza run riuscite, se ha una bozza da riprendere.
  const hasDraft = getOptimizationDraft(appState) !== null;
  const hasCompletedRun = hasSucceededRun(appState);
  if (hasCompletedRun || hasDraft) {
    return { canLoad: true, hasDraft, hasCompletedRun };
  }

  return {
    canLoad: false,
    loadDisabledReason: 'Nessuna ottimizzazione completata',
  };
}

export const initializeThread = createAsyncThunk<
  { threadId: string; thread: SophiaThread } | null,
  void,
  { state: RootState }
>(
  'thread/initialize',
  async (_, { getState, dispatch }) => {
    const current = getState().thread.threadId;

    // Check if there's a stored thread ID
    const stored = current || readStoredThreadId();

    if (!stored) {
      // No thread exists, return null to show welcome page
      return null;
    }

    // Validate that the stored thread ID is still valid
    try {
      const thread = await getThread(stored);
      return { threadId: stored, thread };
    } catch (error) {
      // SPJ-1372: il progetto salvato e' in uso da un altro utente -> si resta in home
      if (error instanceof ProjectLockedError) {
        clearStoredThreadId();
        dispatch(projectLocked(lockedInfo(stored, error)));
        return null;
      }
      // If thread not found (404), clear storage and return null
      if (isThreadNotFoundError(error)) {
        console.warn('Stored thread ID not found (404), clearing storage');
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(STORAGE_KEY);
          } catch (storageError) {
            console.warn('Unable to clear invalid thread id from localStorage', storageError);
          }
        }
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { status } = getState().thread;
      // Prevent multiple simultaneous initialization calls
      if (status === 'loading') {
        return false;
      }
      return true;
    },
  }
);

export const fetchThread = createAsyncThunk<
  { threadId: string; thread: SophiaThread },
  void,
  { state: RootState }
>('thread/fetch', async (_, { getState }) => {
  const state = getState().thread;
  const threadId = await ensureThreadId(state.threadId);
  const thread = await getThread(threadId);
  return { threadId, thread };
});

export const executeSophiaAction = createAsyncThunk<
  { threadId: string; data: unknown; thread?: SophiaThread; refreshThread: boolean },
  { actionName: string; params?: Record<string, unknown>; refreshThread?: boolean },
  { state: RootState }
>('thread/executeSophiaAction', async ({ actionName, params, refreshThread = true }, { getState, dispatch }) => {
  const state = getState().thread;

  // If we already have a threadId and threadData with messageId, use it directly
  // This avoids unnecessary validation calls to the API
  let threadId = state.threadId;
  let threadBefore = state.threadData;
  let messageId = threadBefore?.local_memory?.thread_state?.['current-message'];

  if (!threadId || !messageId) {
    // Only validate/ensure thread if we don't have both threadId and messageId
    threadId = await ensureThreadId(state.threadId);

    if (!messageId) {
      threadBefore = await getThread(threadId);
      messageId = threadBefore.local_memory?.thread_state?.['current-message'];
      if (!messageId) {
        throw new Error('Sophia thread is missing the current message identifier');
      }
    }
  }

  let data: unknown;
  try {
    data = await performAction({
      threadId,
      messageId,
      actionName,
      params,
    });
  } catch (error) {
    // SPJ-1372: il lock e' stato perso (scaduto e preso da un altro utente)
    if (error instanceof ProjectLockedError) {
      dispatch(projectLocked(lockedInfo(threadId, error)));
    }
    throw error;
  }

  let updatedThread: SophiaThread | undefined;
  if (refreshThread) {
    try {
      updatedThread = await getThread(threadId);
    } catch (error) {
      // If we can't refresh the thread after the action, handle based on error type
      if (isThreadNotFoundError(error)) {
        // Thread was deleted after the action - this is unusual but not critical
        console.warn('Thread not found when refreshing after action, but action succeeded:', error);
      } else {
        // For other errors (500, network, etc.), log but don't fail the action
        // The action itself succeeded, so we should return the result
        console.warn('Failed to refresh thread after action, but action succeeded:', error);
      }
    }
  }

  if (!state.threadId) {
    writeStoredThreadId(threadId);
  }
  return { threadId, data, thread: updatedThread, refreshThread };
});

export const loadAvailableProjects = createAsyncThunk<
  ProjectItem[],
  void,
  { state: RootState }
>('thread/loadAvailableProjects', async (_, { getState }) => {
  let sophiaCollectionId = import.meta.env.VITE_SOPHIA_COLLECTION_ID as string | undefined;

  // If collection ID is not in environment, try to get it from the current thread
  if (!sophiaCollectionId) {
    const state = getState().thread;
    const currentThread = state.threadData;

    if (currentThread) {
      // Check if the thread object contains collection information
      const collectionFromThread = currentThread.collection_uuid ||
                                 currentThread.local_memory?.collection_uuid ||
                                 (currentThread as any).collection;

      if (collectionFromThread) {
        sophiaCollectionId = collectionFromThread;
      }
    }
  }

  if (!sophiaCollectionId) {
    throw new Error('VITE_SOPHIA_COLLECTION_ID is not configured');
  }

  // SPJ-1372: una sola chiamata restituisce tutti i progetti della collection (niente piu' N+1).
  const summaries = await getProjectsByCollection(sophiaCollectionId);

  return summaries.map((summary): ProjectItem => {
    const projectType = summary.projectType ?? undefined;
    const lockedByName = summary.lockedByName || summary.lockedByEmail;

    // Per le ottimizzazioni si riusa la logica di loadability esistente ricostruendo un
    // appState minimale coi soli campi che serve (history / pending-config-id / optim-dataset).
    const loadability = projectType === 'optimization'
      ? getOptimizationProjectLoadability({
          history: summary.history ?? [],
          'pending-config-id': summary.pendingConfigId ?? undefined,
          'optim-dataset': summary.optimDataset ?? undefined,
        })
      : { canLoad: true };

    return {
      threadId: summary.threadId,
      name: summary.title || 'Untitled Project',
      lastEditTime: Number(summary.lastEditTime) || 0,
      projectType,
      catalogMode: summary.catalogMode ?? undefined,
      season: summary.season ?? undefined,
      optimizationId: summary.optimizationId ?? undefined,
      consegna: summary.consegna ?? undefined,
      macrotema: summary.macrotema ?? undefined,
      gender: summary.gender ?? undefined,
      creatorName: summary.creatorName ?? undefined,
      creatorEmail: summary.creatorEmail ?? undefined,
      lastEditorName: summary.lastEditorName ?? summary.lastEditorEmail ?? undefined,
      lastEditorEmail: summary.lastEditorEmail ?? undefined,
      lastEditorTime: summary.lastEditorTime ?? undefined,
      lockedByName: lockedByName ?? undefined,
      incognito: summary.incognito,
      ...loadability,
      // SPJ-1372: un progetto in uso da un altro utente non si puo' aprire
      ...(lockedByName ? { canLoad: false, loadDisabledReason: `In uso da ${lockedByName}` } : {}),
    };
  });
});

export const loadProject = createAsyncThunk<
  { threadId: string; thread: SophiaThread },
  string,
  { state: RootState }
>('thread/loadProject', async (threadId, { dispatch }) => {
  let thread: SophiaThread;
  try {
    thread = await getThread(threadId);
  } catch (error) {
    // SPJ-1372: progetto in uso da un altro utente -> non si apre
    if (error instanceof ProjectLockedError) {
      dispatch(projectLocked(lockedInfo(threadId, error)));
    }
    throw error;
  }
  writeStoredThreadId(threadId);
  return { threadId, thread };
});

export const deleteProject = createAsyncThunk<
  string,
  string,
  { state: RootState }
>('thread/deleteProject', async (threadId, { getState }) => {
  await deleteThread(threadId);

  // If the deleted thread is the current one, clear it from state and storage
  const currentThreadId = getState().thread.threadId;
  if (currentThreadId === threadId) {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.warn('Unable to clear thread id from localStorage', error);
      }
    }
  }

  return threadId;
});

export const createNewProject = createAsyncThunk<
  { threadId: string; thread: SophiaThread },
  { title: string; creationMetadata: CreationMetadata },
  { state: RootState }
>('thread/createNewProject', async ({ title, creationMetadata }) => {
  const threadId = await createThread(title, creationMetadata);
  writeStoredThreadId(threadId);
  const thread = await getThread(threadId);
  return { threadId, thread };
});

const threadSlice = createSlice({
  name: 'thread',
  initialState,
  reducers: {
    setThreadId(state, action: PayloadAction<string>) {
      state.threadId = action.payload;
      writeStoredThreadId(action.payload);
    },
    setHasUnsavedMpChanges(state, action: PayloadAction<boolean>) {
      state.hasUnsavedMpChanges = action.payload;
    },
    clearLockedProject(state) {
      state.lockedProject = null;
    },
    resetThread(state) {
      state.threadId = null;
      state.threadData = null;
      state.status = 'idle';
      state.error = null;
      state.hasUnsavedMpChanges = false;
      // Clear from localStorage
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
          console.warn('Unable to clear thread id from localStorage', error);
        }
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(projectLocked, (state, action) => {
        state.lockedProject = action.payload;
      })
      .addCase(initializeThread.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(initializeThread.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          state.threadId = action.payload.threadId;
          state.threadData = action.payload.thread;
        }
        state.error = null;
      })
      .addCase(initializeThread.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Unable to initialise Sophia thread';
      })
      .addCase(fetchThread.fulfilled, (state, action) => {
        state.threadId = action.payload.threadId;
        state.threadData = action.payload.thread;
      })
      .addCase(fetchThread.rejected, (state, action) => {
        state.error = action.error.message ?? state.error;
      })
      .addCase(createNewProject.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createNewProject.fulfilled, (state, action) => {
        state.threadId = action.payload.threadId;
        state.threadData = action.payload.thread;
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(createNewProject.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to create project';
      })
      .addCase(executeSophiaAction.fulfilled, (state, action) => {
        state.threadId = action.payload.threadId;
        if (action.payload.refreshThread && action.payload.thread) {
          state.threadData = action.payload.thread;
        }
      })
      .addCase(executeSophiaAction.rejected, (state, action) => {
        state.error = action.error.message ?? state.error;
      })
      .addCase(loadAvailableProjects.pending, (state) => {
        state.projectsStatus = 'loading';
        state.projectsError = null;
      })
      .addCase(loadAvailableProjects.fulfilled, (state, action) => {
        state.projectsStatus = 'succeeded';
        state.availableProjects = action.payload;
        state.projectsError = null;
      })
      .addCase(loadAvailableProjects.rejected, (state, action) => {
        state.projectsStatus = 'failed';
        state.projectsError = action.error.message ?? 'Failed to load projects';
      })
      .addCase(loadProject.fulfilled, (state, action) => {
        state.threadId = action.payload.threadId;
        state.threadData = action.payload.thread;
        state.status = 'succeeded';
        state.error = null;
      })
      .addCase(loadProject.rejected, (state, action) => {
        state.error = action.error.message ?? 'Failed to load project';
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        // Remove the deleted project from the available projects list
        state.availableProjects = state.availableProjects.filter(
          project => project.threadId !== action.payload
        );

        // If the deleted project is the current one, reset the thread state
        if (state.threadId === action.payload) {
          state.threadId = null;
          state.threadData = null;
          state.status = 'idle';
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.projectsError = action.error.message ?? 'Failed to delete project';
      });
  },
});

export const { setThreadId, resetThread, setHasUnsavedMpChanges, clearLockedProject } = threadSlice.actions;

export const selectThread = (state: RootState) => state.thread;

export const selectHasUnsavedMpChanges = createSelector(selectThread, thread => thread.hasUnsavedMpChanges);

export const selectThreadError = createSelector(selectThread, thread => thread.error);

export const selectLockedProject = createSelector(selectThread, thread => thread.lockedProject);

export const selectThreadStatus = createSelector(selectThread, thread => thread.status);

export const selectThreadAppState = createSelector(selectThread, thread => {
  return (thread.threadData?.local_memory?.thread_state?.app_state as Record<string, unknown> | undefined) ?? {};
});

export const selectCurrentProjectType = createSelector(selectThread, thread => {
  return getProjectTypeFromThread(thread.threadData) ?? 'consultation';
});

export const selectCurrentCatalogMode = createSelector(selectThread, thread => {
  return getCatalogModeFromThread(thread.threadData);
});

export const selectSelectedProducts = createSelector(selectThreadAppState, appState => {
  const selected = appState?.['global-select-product'];
  if (Array.isArray(selected)) {
    return selected as string[];
  }
  return [];
});

export const selectProjectName = createSelector(selectThreadAppState, appState => {
    const selected = appState?.['global-project-name'];
    if (typeof selected === 'string') {
      return selected as string;
    }
    return '';
  });

export const selectSelectedCount = createSelector(selectSelectedProducts, selected => selected.length);

export const selectOpenDetails = createSelector(selectThreadAppState, appState => {
  const openDetails = appState?.['global-open-details'];
  return typeof openDetails === 'string' ? openDetails : null;
});

export const selectCurrentLastEditTime = createSelector(selectThread, thread => {
  const editTime = thread.threadData?.local_memory?.thread_state?.['current-last-edit-time'];
  return typeof editTime === 'number' ? editTime : null;
});

export const selectAvailableProjects = createSelector(selectThread, thread => thread.availableProjects);

export const selectProjectsStatus = createSelector(selectThread, thread => thread.projectsStatus);

export const selectProjectsError = createSelector(selectThread, thread => thread.projectsError);

export default threadSlice.reducer;
